import SiteLayout from '@/components/feature/SiteLayout';
import Breadcrumbs from '@/components/feature/Breadcrumbs';
import FaqAccordion from '@/components/feature/FaqAccordion';
import JsonLd from '@/components/feature/JsonLd';
import { useDocumentMeta } from '@/hooks/useDocumentMeta';
import { aboutValues, faqs, principles, siteInfo, stats, testimonials } from '@/mocks/site';
import ContactForm from './components/ContactForm';

const aboutSchema = [
  {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    name: '关于爆料短剧',
    description: '爆料短剧是面向短剧观众的更新追踪内容站，专注连续剧集更新顺序、追剧日历与分集剧情介绍。',
    inLanguage: 'zh-CN',
    url: 'https://jinghuibt.cn/about',
  },
  {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: '爆料短剧',
    url: 'https://jinghuibt.cn/',
    email: siteInfo.email,
    description: siteInfo.intro,
    foundingDate: siteInfo.since,
  },
  {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map((item) => ({
      '@type': 'Question',
      name: item.q,
      acceptedAnswer: { '@type': 'Answer', text: item.a },
    })),
  },
];

export default function AboutPage() {
  useDocumentMeta({
    title: '关于我们｜爆料短剧的定位、内容原则与联系方式 - 爆料短剧',
    description:
      '了解爆料短剧：我们如何整理连续剧集更新顺序、维护追剧日历与分集剧情介绍，以及内容原则、用户口碑和联系邮箱，欢迎推荐你想追的短剧。',
    keywords: '关于爆料短剧,短剧更新追踪,追剧日历,内容原则,联系方式',
    path: '/about',
    ogType: 'website',
  });

  const [featuredQuote, ...restQuotes] = testimonials;

  return (
    <SiteLayout>
      {aboutSchema.map((schema, index) => (
        <JsonLd key={index} data={schema} />
      ))}

      <section className="w-full border-b border-background-200 bg-background-100 py-8 md:py-12">
        <div className="mx-auto w-full max-w-content px-4 md:px-6">
          <Breadcrumbs items={[{ label: '关于我们' }]} />
          <h1 className="mt-5 font-heading text-2xl font-black text-foreground-950 md:text-4xl">
            关于爆料短剧
          </h1>
          <p className="mt-3 max-w-3xl text-sm leading-7 text-foreground-600 md:text-base">
            我们不是聚合播放站，只想认真做一份「打开就知道更到哪儿」的更新追踪表。
          </p>
        </div>
      </section>

      <section className="w-full border-b border-background-200 bg-background-50 py-12 md:py-16">
        <div className="mx-auto w-full max-w-content px-4 md:px-6">
          <div className="grid grid-cols-1 gap-10 lg:grid-cols-12">
            <div className="lg:col-span-7">
              <p className="mb-2 flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-primary-600">
                <span className="h-px w-8 bg-primary-400" aria-hidden="true" />
                Who We Are
              </p>
              <h2 className="font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
                一份写给追剧人的更新追踪表
              </h2>
              <p className="mt-5 text-sm leading-8 text-foreground-700 md:text-[15px]">{siteInfo.intro}</p>
              <p className="mt-4 text-sm leading-8 text-foreground-700 md:text-[15px]">
                短剧的更新常常分散在不同平台，档期也随时可能调整。我们把信息重新按「顺序」组织：
                先给出连续剧集更新顺序，再补上追剧日历与分集剧情介绍，最后用全季剧情梗概收束整条主线。
                这样一来，无论你是当天追更，还是完结后补剧，都能找到清晰的入口。
              </p>

              <dl className="mt-8 grid grid-cols-2 gap-6 border-t border-background-200 pt-6 sm:grid-cols-4">
                {stats.map((item) => (
                  <div key={item.label}>
                    <dt className="text-xs text-foreground-500">{item.label}</dt>
                    <dd className="font-display mt-1 text-2xl text-foreground-950 md:text-3xl">
                      {item.value}
                      <span className="ml-0.5 text-sm text-primary-600">{item.unit}</span>
                    </dd>
                  </div>
                ))}
              </dl>
            </div>

            <div className="lg:col-span-5">
              <div className="h-[300px] w-full overflow-hidden rounded-lg border border-background-200 bg-background-200 md:h-[380px]">
                <img
                  src="https://readdy.ai/api/search-image?query=Warm%20editorial%20flat%20lay%20of%20a%20cozy%20living%20room%20corner%20with%20a%20notebook%20calendar%20and%20a%20cup%20of%20tea%2C%20soft%20amber%20afternoon%20light%2C%20cream%20and%20terracotta%20tones%2C%20minimal%20lifestyle%20photography%2C%20calm%20and%20tidy%20composition%2C%20high%20detail&width=900&height=1100&seq=about-baoliao-duanju&orientation=portrait"
                  alt="爆料短剧编辑整理追剧日历的工作台"
                  title="爆料短剧 更新追踪与追剧日历整理"
                  className="h-full w-full object-top"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="w-full border-b border-background-200 bg-background-100 py-12 md:py-16">
        <div className="mx-auto w-full max-w-content px-4 md:px-6">
          <h2 className="font-heading text-2xl font-bold text-foreground-950 md:text-3xl">网站定位</h2>
          <div className="mt-8 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {aboutValues.map((item, index) => (
              <div
                key={item.title}
                className={`rounded-lg border p-5 ${
                  index === 0 ? 'border-accent-300 bg-accent-50' : 'border-background-200 bg-background-50'
                }`}
              >
                <span className="font-display text-3xl text-background-400">0{index + 1}</span>
                <h3 className="mt-3 font-heading text-lg font-bold text-foreground-950">{item.title}</h3>
                <p className="mt-2 text-sm leading-7 text-foreground-600">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="w-full border-b border-background-200 bg-background-50 py-12 md:py-16">
        <div className="mx-auto w-full max-w-content px-4 md:px-6">
          <h2 className="font-heading text-2xl font-bold text-foreground-950 md:text-3xl">内容原则</h2>
          <p className="mt-3 max-w-3xl text-sm leading-7 text-foreground-600">
            这几条原则决定了我们怎么写更新顺序、怎么处理剧情介绍，也决定了哪些内容我们不会写。
          </p>
          <ul className="mt-8 grid grid-cols-1 gap-5 sm:grid-cols-2">
            {principles.map((item) => (
              <li key={item.title} className="flex gap-4 rounded-lg border border-background-200 bg-background-50 p-5">
                <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-md bg-primary-50 text-primary-600">
                  <i className={`${item.icon} text-lg`} aria-hidden="true" />
                </span>
                <div>
                  <h3 className="font-heading text-base font-bold text-foreground-950">{item.title}</h3>
                  <p className="mt-1.5 text-sm leading-7 text-foreground-600">{item.desc}</p>
                </div>
              </li>
            ))}
          </ul>
        </div>
      </section>

      <section className="w-full border-b border-background-200 bg-secondary-950 py-12 md:py-16">
        <div className="mx-auto w-full max-w-content px-4 md:px-6">
          <h2 className="font-heading text-2xl font-bold text-background-50 md:text-3xl">用户口碑</h2>
          <div className="mt-8 grid grid-cols-1 gap-6 lg:grid-cols-12">
            <figure className="rounded-lg border border-secondary-800 bg-secondary-900 p-6 md:p-8 lg:col-span-7">
              <i className="ri-double-quotes-l text-3xl text-accent-500" aria-hidden="true" />
              <blockquote className="mt-4 text-base leading-8 text-background-100 md:text-lg">
                {featuredQuote.text}
              </blockquote>
              <figcaption className="mt-6 flex items-center gap-3">
                <span className="h-11 w-11 overflow-hidden rounded-full bg-secondary-800">
                  <img src={featuredQuote.avatar} alt={`${featuredQuote.name}的头像`} title={featuredQuote.name} className="h-full w-full object-top" />
                </span>
                <span>
                  <span className="block text-sm text-background-50">{featuredQuote.name}</span>
                  <span className="block text-xs text-background-400">{featuredQuote.role}</span>
                </span>
              </figcaption>
            </figure>

            <div className="flex flex-col gap-6 lg:col-span-5">
              {restQuotes.map((item) => (
                <figure key={item.name} className="flex-1 rounded-lg border border-secondary-800 p-5">
                  <blockquote className="text-sm leading-7 text-background-200">{item.text}</blockquote>
                  <figcaption className="mt-4 flex items-center gap-3">
                    <span className="h-9 w-9 overflow-hidden rounded-full bg-secondary-800">
                      <img src={item.avatar} alt={`${item.name}的头像`} title={item.name} className="h-full w-full object-top" />
                    </span>
                    <span className="text-xs text-background-400">
                      {item.name} · {item.role}
                    </span>
                  </figcaption>
                </figure>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="w-full border-b border-background-200 bg-background-100 py-12 md:py-16">
        <div className="mx-auto w-full max-w-content px-4 md:px-6">
          <div className="grid grid-cols-1 gap-8 lg:grid-cols-12">
            <div className="lg:col-span-5">
              <p className="mb-2 flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-primary-600">
                <span className="h-px w-8 bg-primary-400" aria-hidden="true" />
                Contact
              </p>
              <h2 className="font-heading text-2xl font-bold text-foreground-950 md:text-3xl">推荐短剧与联系我们</h2>
              <p className="mt-4 text-sm leading-7 text-foreground-600">
                想让我们追踪某部短剧，或者发现某部剧的更新顺序有误？把你的建议发给我们，
                也可以直接发邮件。我们会核对平台更新节奏后确认是否收录。
              </p>
              <ul className="mt-6 space-y-3 text-sm text-foreground-700">
                <li className="flex items-center gap-2">
                  <i className="ri-mail-line text-primary-500" aria-hidden="true" />
                  {siteInfo.email}
                </li>
                <li className="flex items-center gap-2">
                  <i className="ri-time-line text-primary-500" aria-hidden="true" />
                  我们通常在每个工作日查看一次留言
                </li>
                <li className="flex items-center gap-2">
                  <i className="ri-shield-check-line text-primary-500" aria-hidden="true" />
                  不会将你的联系方式用于其他用途
                </li>
              </ul>
            </div>
            <div className="lg:col-span-7">
              <ContactForm />
            </div>
          </div>
        </div>
      </section>

      <section className="w-full bg-background-50 py-12 md:py-16">
        <div className="mx-auto w-full max-w-content px-4 md:px-6">
          <h2 className="font-heading text-2xl font-bold text-foreground-950 md:text-3xl">常见问题</h2>
          <p className="mt-3 text-sm text-foreground-600">关于内容整理与更新节奏，先看看这里有没有答案。</p>
          <div className="mt-8">
            <FaqAccordion items={faqs} />
          </div>
        </div>
      </section>
    </SiteLayout>
  );
}